import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { MainFilterHandler } from "../../../components/comman/MainFilterHandler";
import { formatAmount } from "../../../utility/CommonFunctions";
import { RoutesLink } from "../../../ApiHelper/RoutesLink";
import ViewIcon from "../../../components/Icons/ViewIcon";

export default function InvoiceKpiLedgerModal({
  isOpen,
  toggle,
  kpiType,
  title,
  themeColor = "#3b82f6",
  filterDetails,
  selectedMonth,
  siteId,
  companyId,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (isOpen && kpiType) {
      fetchLedgerData();
      setSearchQuery("");
    }
  }, [isOpen, kpiType, filterDetails?.year, selectedMonth, siteId, companyId]);

  const fetchLedgerData = () => {
    setLoading(true);
    const { sortByDate, months, sortBySingleDate, startDate, endDate, currentPage, pageSize, count, totalPages, ...restFilterDetails } = filterDetails || {};
    
    const params = {
      ...restFilterDetails,
      siteId: siteId || filterDetails?.siteId,
      companyId: companyId || filterDetails?.companyId,
      pageSize: 2000,
      pageNumber: 1,
    };

    let queryStr = MainFilterHandler(params);
    if (selectedMonth) {
      queryStr += `${queryStr ? "&" : ""}month=${selectedMonth}`;
    }

    const API = `${apiUrl.get_invoice}?${queryStr}`;

    callApi(API, { method: "GET" })
      .then((response) => {
        setLoading(false);
        if (response?.status === true && response?.data?.invoice) {
          const rawInvoices = response.data.invoice || [];
          setInvoices(rawInvoices);
        } else {
          setInvoices([]);
        }
      })
      .catch(() => {
        setLoading(false);
        setInvoices([]);
      });
  };

  // Filter invoices based on KPI type and in-modal search
  const filteredInvoices = useMemo(() => {
    return invoices.filter((item) => {
      const tds = parseFloat(item?.tdsAmount || 0);
      const retention = parseFloat(item?.rententionAmount || item?.retentionAmount || 0);
      const taxable = parseFloat(item?.taxableAmount || 0);
      const total = parseFloat(item?.totalAmount || 0);
      const gst = Math.max(0, total - taxable);
      const received = parseFloat(item?.receivedAmount || 0);
      const gross = parseFloat(item?.grossAmount || total || 0);
      const due = Math.max(0, gross - received);

      let matchesType = true;
      if (kpiType === "tds") {
        matchesType = tds > 0;
      } else if (kpiType === "retention") {
        matchesType = retention > 0;
      } else if (kpiType === "gst") {
        matchesType = gst > 0;
      } else if (kpiType === "received") {
        matchesType = received > 0;
      } else if (kpiType === "due") {
        matchesType = due > 0;
      }

      if (!matchesType) return false;

      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      const invNo = (item?.invoiceNumber || "").toLowerCase();
      const siteName = (item?.workorder?.site?.name || "").toLowerCase();
      const woNo = (item?.workorder?.workOrderNumber || "").toLowerCase();
      const customer = (item?.workorder?.site?.customer?.name || "").toLowerCase();

      return (
        invNo.includes(q) ||
        siteName.includes(q) ||
        woNo.includes(q) ||
        customer.includes(q)
      );
    });
  }, [invoices, kpiType, searchQuery]);

  // Aggregate totals for the filtered ledger
  const totals = useMemo(() => {
    let totalTaxable = 0;
    let totalMetric = 0;
    let totalGross = 0;
    let totalReceived = 0;

    filteredInvoices.forEach((item) => {
      const taxable = parseFloat(item?.taxableAmount || 0);
      const total = parseFloat(item?.totalAmount || 0);
      const tds = parseFloat(item?.tdsAmount || 0);
      const retention = parseFloat(item?.rententionAmount || item?.retentionAmount || 0);
      const gst = Math.max(0, total - taxable);
      const received = parseFloat(item?.receivedAmount || 0);
      const gross = parseFloat(item?.grossAmount || total || 0);

      totalTaxable += taxable;
      totalGross += gross;
      totalReceived += received;

      if (kpiType === "tds") totalMetric += tds;
      else if (kpiType === "retention") totalMetric += retention;
      else if (kpiType === "gst") totalMetric += gst;
      else if (kpiType === "received") totalMetric += received;
      else if (kpiType === "due") totalMetric += Math.max(0, gross - received);
      else totalMetric += total;
    });

    return {
      count: filteredInvoices.length,
      totalTaxable,
      totalMetric,
      totalGross,
      totalReceived,
    };
  }, [filteredInvoices, kpiType]);

  const getMetricLabel = () => {
    switch (kpiType) {
      case "tds":
        return "TDS Amount";
      case "retention":
        return "Retention Amount";
      case "gst":
        return "GST Amount";
      case "received":
        return "Received Amount";
      case "due":
        return "Payment Due";
      case "taxable":
        return "Taxable Amount";
      default:
        return "Amount";
    }
  };

  const getMetricValue = (item) => {
    const taxable = parseFloat(item?.taxableAmount || 0);
    const total = parseFloat(item?.totalAmount || 0);
    const tds = parseFloat(item?.tdsAmount || 0);
    const retention = parseFloat(item?.rententionAmount || item?.retentionAmount || 0);
    const gst = Math.max(0, total - taxable);
    const received = parseFloat(item?.receivedAmount || 0);
    const gross = parseFloat(item?.grossAmount || total || 0);

    switch (kpiType) {
      case "tds":
        return tds;
      case "retention":
        return retention;
      case "gst":
        return gst;
      case "received":
        return received;
      case "due":
        return Math.max(0, gross - received);
      case "taxable":
        return taxable;
      default:
        return total;
    }
  };

  // Export CSV of this specific ledger
  const exportCsv = () => {
    if (!filteredInvoices.length) return;

    const headers = [
      "Sr No",
      "Invoice No",
      "Invoice Date",
      "Site Name",
      "Work Order",
      "Taxable Amount",
      getMetricLabel(),
      "Gross Amount",
      "Received Amount",
    ];

    const rows = filteredInvoices.map((item, index) => [
      index + 1,
      `"${item?.invoiceNumber || ""}"`,
      `"${item?.invoiceDate ? moment(item.invoiceDate).format("DD-MM-YYYY") : ""}"`,
      `"${item?.workorder?.site?.name || ""}"`,
      `"${item?.workorder?.workOrderNumber || ""}"`,
      parseFloat(item?.taxableAmount || 0).toFixed(2),
      getMetricValue(item).toFixed(2),
      parseFloat(item?.grossAmount || item?.totalAmount || 0).toFixed(2),
      parseFloat(item?.receivedAmount || 0).toFixed(2),
    ]);

    // Add summary row
    rows.push([
      "Total",
      "",
      "",
      "",
      "",
      totals.totalTaxable.toFixed(2),
      totals.totalMetric.toFixed(2),
      totals.totalGross.toFixed(2),
      totals.totalReceived.toFixed(2),
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `Invoice_${kpiType.toUpperCase()}_Ledger_${selectedMonth || "All"}_${filterDetails?.year || ""}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      centered
      scrollable
      className="invoice-kpi-ledger-modal"
    >
      <ModalHeader
        toggle={toggle}
        style={{
          borderBottom: `2px solid ${themeColor}20`,
          backgroundColor: `${themeColor}08`,
          padding: "16px 24px",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: themeColor,
            }}
          />
          <h5 className="mb-0 fw-bold" style={{ color: "#1e293b", fontSize: "18px" }}>
            {title || `${getMetricLabel()} Breakdown Ledger`}
          </h5>
          <span
            className="badge rounded-pill ms-2"
            style={{
              backgroundColor: `${themeColor}20`,
              color: themeColor,
              fontWeight: "600",
              fontSize: "12px",
            }}
          >
            {selectedMonth ? `${selectedMonth.toUpperCase()}` : "All Months"} {filterDetails?.year ? `(${filterDetails.year})` : ""}
          </span>
        </div>
      </ModalHeader>

      <ModalBody style={{ padding: "20px 24px", minHeight: "350px" }}>
        {/* KPI Summary Banner */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 rounded mb-3"
          style={{
            backgroundColor: "#f8fafc",
            border: "1px solid #e2e8f0",
          }}
        >
          <div className="d-flex align-items-center gap-4 flex-wrap">
            <div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Total Contributing Invoices
              </div>
              <div className="fw-bold fs-5 text-dark">
                {totals.count} <span style={{ fontSize: "12px", fontWeight: "normal", color: "#64748b" }}>records</span>
              </div>
            </div>

            <div style={{ borderLeft: "1px solid #cbd5e1", height: "32px" }} />

            <div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Total {getMetricLabel()}
              </div>
              <div className="fw-bold fs-5" style={{ color: themeColor }}>
                {formatAmount(totals.totalMetric)}
              </div>
            </div>

            <div style={{ borderLeft: "1px solid #cbd5e1", height: "32px" }} />

            <div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Total Taxable Value
              </div>
              <div className="fw-bold fs-6 text-secondary">
                {formatAmount(totals.totalTaxable)}
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search invoice, site..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ minWidth: "220px", fontSize: "13px" }}
            />
            <button
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={exportCsv}
              disabled={filteredInvoices.length === 0}
              title="Export this breakdown to CSV"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
              </svg>
              Export
            </button>
          </div>
        </div>

        {/* Ledger Table */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <div className="mt-2 text-muted" style={{ fontSize: "13px" }}>
              Loading ledger details...
            </div>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p className="mb-0 fs-6">No matching invoices found with {getMetricLabel()} for this selection.</p>
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: "420px", overflowY: "auto" }}>
            <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
              <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                <tr>
                  <th style={{ width: "60px" }}>SR NO.</th>
                  <th>INV NO.</th>
                  <th>INVOICE DATE</th>
                  <th>SITE</th>
                  <th>WORK ORDER</th>
                  <th className="text-end">TAXABLE AMOUNT</th>
                  <th className="text-end" style={{ color: themeColor, fontWeight: "700" }}>
                    {getMetricLabel().toUpperCase()}
                  </th>
                  <th className="text-end">GROSS AMOUNT</th>
                  <th className="text-center" style={{ width: "80px" }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((item, index) => {
                  const metricVal = getMetricValue(item);
                  return (
                    <tr key={item?.id || index}>
                      <td>{String(index + 1).padStart(2, "0")}</td>
                      <td>
                        <span
                          className="text-primary fw-semibold cursor-pointer"
                          style={{ cursor: "pointer", textDecoration: "underline" }}
                          onClick={() => {
                            toggle();
                            navigate(RoutesLink.invoice_detail + `/${item?.id}`);
                          }}
                        >
                          {item?.invoiceNumber || "-"}
                        </span>
                      </td>
                      <td>
                        {item?.invoiceDate
                          ? moment(item?.invoiceDate).format("DD-MM-YYYY")
                          : "-"}
                      </td>
                      <td>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: "220px" }}>
                          {item?.workorder?.site?.name || "-"}
                        </div>
                        {item?.workorder?.site?.customer?.name && (
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            {item?.workorder?.site?.customer?.name}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {item?.workorder?.workOrderNumber || "-"}
                        </span>
                      </td>
                      <td className="text-end">
                        {formatAmount(item?.taxableAmount || 0)}
                      </td>
                      <td
                        className="text-end fw-bold"
                        style={{
                          color: themeColor,
                          backgroundColor: `${themeColor}0d`,
                        }}
                      >
                        {formatAmount(metricVal)}
                      </td>
                      <td className="text-end">
                        {formatAmount(item?.grossAmount || item?.totalAmount || 0)}
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary p-1 border-0"
                          title="View Invoice Detail"
                          onClick={() => {
                            toggle();
                            navigate(RoutesLink.invoice_detail + `/${item?.id}`);
                          }}
                        >
                          <ViewIcon />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="table-light sticky-bottom fw-bold" style={{ borderTop: "2px solid #cbd5e1" }}>
                <tr>
                  <td colSpan={5} className="text-end">
                    TOTAL ({totals.count} Invoices):
                  </td>
                  <td className="text-end">{formatAmount(totals.totalTaxable)}</td>
                  <td className="text-end fw-bolder" style={{ color: themeColor, fontSize: "14px" }}>
                    {formatAmount(totals.totalMetric)}
                  </td>
                  <td className="text-end">{formatAmount(totals.totalGross)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between align-items-center py-2 px-4 border-top">
        <div className="text-muted" style={{ fontSize: "12px" }}>
          Showing detailed ledger breakdown for <strong>{getMetricLabel()}</strong>
        </div>
        <button className="btn btn-secondary btn-sm px-4" onClick={toggle}>
          Close
        </button>
      </ModalFooter>
    </Modal>
  );
}
